import React from 'react';

import WithConfirm from '~/components/ConfirmModal';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import SortableTable from '~/components/core/Tables/SortableTable';
import PencilIcon from '~/components/icons/PencilIcon';
import TrashIcon from '~/components/icons/TrashIcon';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import type { AuthorityLevelInfo } from '~/server_shared/openapi/data-contracts';

interface AuthorityLevelsTableProps {
  levels: AuthorityLevelInfo[];
  arePaymentLimitsSplit: boolean;
  onEdit: (level: AuthorityLevelInfo) => void;
  onDelete: (level: AuthorityLevelInfo) => void;
}

const formatLimit = (raw: string): string => (Number(raw) === -1 ? TEXTS.UNLIMITED : Number(raw).toLocaleString());

const splitLimitColumns = [
  {
    id: 'indemnity_payments_limit',
    label: TEXTS.COLUMN_INDEMNITY_PAYMENTS,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.indemnity_payments_limit),
  },
  {
    id: 'expenses_payments_limit',
    label: TEXTS.COLUMN_EXPENSE_PAYMENTS,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.expenses_payments_limit),
  },
  {
    id: 'indemnity_reserves_limit',
    label: TEXTS.COLUMN_INDEMNITY_RESERVES,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.indemnity_reserves_limit),
  },
  {
    id: 'expenses_reserves_limit',
    label: TEXTS.COLUMN_EXPENSE_RESERVES,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.expenses_reserves_limit),
  },
];

const unifiedLimitColumns = [
  {
    id: 'payments_limit',
    label: TEXTS.COLUMN_PAYMENTS,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.payments_limit),
  },
  {
    id: 'reserves_limit',
    label: TEXTS.COLUMN_RESERVES,
    disableSort: true,
    specialCell: (row: AuthorityLevelInfo) => formatLimit(row.limits.reserves_limit),
  },
];

const AuthorityLevelsTable: React.FC<AuthorityLevelsTableProps> = ({
  levels,
  arePaymentLimitsSplit,
  onEdit,
  onDelete,
}) => {
  const columns = [
    { id: 'name', label: TEXTS.COLUMN_NAME },
    { id: 'key', label: TEXTS.COLUMN_KEY },
    ...(arePaymentLimitsSplit ? splitLimitColumns : unifiedLimitColumns),
    {
      id: 'assigned_user_count',
      label: TEXTS.COLUMN_USERS,
      width: '120px',
    },
    {
      id: 'actions',
      label: '',
      width: '100px',
      disableSort: true,
      specialCell: (row: AuthorityLevelInfo) => (
        <div className="flex gap-4">
          <IconButton onClick={() => onEdit(row)} aria-label={`${TEXTS.EDIT_LEVEL} ${row.name}`}>
            <PencilIcon />
          </IconButton>
          <WithConfirm
            title={TEXTS.DELETE_CONFIRM_TITLE}
            contentText={TEXTS.DELETE_CONFIRM_BODY(row.name)}
            primaryButtonName={TEXTS.DELETE_LEVEL}
            shouldCloseOnPrimary
          >
            <IconButton onClick={() => onDelete(row)} aria-label={`${TEXTS.DELETE_CONFIRM_TITLE} ${row.name}`}>
              <TrashIcon />
            </IconButton>
          </WithConfirm>
        </div>
      ),
    },
  ];

  if (levels.length === 0) {
    return <div className="py-12 text-center text-slate-700">{TEXTS.EMPTY_STATE}</div>;
  }

  return <SortableTable columns={columns} rows={levels} />;
};

export default AuthorityLevelsTable;
